import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { admin, typography } from '../../../../styles/tokens';
import {
  detectCompetitorFromHeaders,
  type CompetitorImportProfile,
} from '../competitor/competitorImportProfiles';
import type { DedupeDecision, ImportBatchResult, NormalizedRow } from '../shared/types';
import type { ImportClientsPath } from '../types';
import UploadStep from './UploadStep';
import MappingStep from './MappingStep';
import PreviewStep from './PreviewStep';
import ImportStep from './ImportStep';
import SuccessStep from './SuccessStep';
import type { ParsedSheet } from './types';

interface SpreadsheetPathProps {
  onBackToGrid: () => void;
  onSaved: () => void;
  // Phase 4 — optional competitor pre-fill. When set, MappingStep pre-maps
  // columns via the profile's columnMap if the uploaded file's headers
  // match the profile's fingerprint (≥3 columns). If the fingerprint does
  // not match, we fall through to generic auto-detection and show a muted
  // notice. Rows written under a matched profile are tagged with the
  // profile's dbSourceValue (e.g. 'import_square'); unmatched rows with a
  // profile still use the profile's source (user chose this card). Null
  // profile with `defaultSourceOverride` lets Path 4's catch-all card tag
  // rows with 'import_competitor' instead of 'import_spreadsheet'.
  competitorProfile?: CompetitorImportProfile | null;
  defaultSourceOverride?: ImportClientsPath;
}

type Step = 'upload' | 'mapping' | 'preview' | 'importing' | 'success';

export default function SpreadsheetPath({
  onBackToGrid,
  onSaved,
  competitorProfile = null,
  defaultSourceOverride,
}: SpreadsheetPathProps) {
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [normalized, setNormalized] = useState<NormalizedRow[]>([]);
  const [decisions, setDecisions] = useState<Record<string, DedupeDecision>>({});
  const [result, setResult] = useState<ImportBatchResult | null>(null);

  // Fingerprint-match the uploaded file against the selected competitor
  // profile. The detector is pure and cheap, so we re-run it from parsed
  // state rather than storing a flag.
  const competitorMatched = useMemo(() => {
    if (!competitorProfile || !parsed) return false;
    const headers = parsed.hasHeader && parsed.rows.length > 0 ? parsed.rows[0] : [];
    const detected = detectCompetitorFromHeaders(headers);
    return detected?.id === competitorProfile.id;
  }, [competitorProfile, parsed]);

  // Resolve the source tag for inserts. If the user came in via a competitor
  // card AND the fingerprint matched → tag with the per-competitor value.
  // If the user picked a competitor card but the file didn't match → still
  // tag with the competitor value (they self-identified). If the user came
  // in via Path 4's catch-all → tag with 'competitor'. Otherwise → default
  // Path 3 'spreadsheet' tag.
  const sourcePath: ImportClientsPath =
    competitorProfile?.id ?? defaultSourceOverride ?? 'spreadsheet';

  const reset = () => {
    setStep('upload');
    setParsed(null);
    setNormalized([]);
    setDecisions({});
    setResult(null);
  };

  const backButton = (
    label: string,
    onClick: () => void,
  ) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 8px 6px 4px',
        marginBottom: 16,
        background: 'transparent',
        border: 'none',
        color: admin.caption,
        fontSize: typography.size.caption,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <ChevronLeft size={16} />
      <span>{label}</span>
    </button>
  );

  const header = (title: string) => (
    <h1
      style={{
        margin: 0,
        fontSize: typography.size.title,
        fontWeight: typography.weight.bold,
        color: admin.header,
        letterSpacing: '-0.01em',
        marginBottom: 8,
      }}
    >
      {title}
    </h1>
  );

  const shell = (title: string, back: React.ReactNode, body: React.ReactNode) => (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '8px 4px 0' }}>
      {back}
      {header(title)}
      <div style={{ marginTop: 16 }}>{body}</div>
    </div>
  );

  if (step === 'upload') {
    return shell(
      'Import from a spreadsheet',
      backButton('Import your clients', onBackToGrid),
      <UploadStep
        onParsed={p => {
          setParsed(p);
          setStep('mapping');
        }}
      />,
    );
  }

  if (step === 'mapping' && parsed) {
    return shell(
      'Map your columns',
      backButton('Upload a different file', () => setStep('upload')),
      <MappingStep
        parsed={parsed}
        competitorProfile={competitorProfile}
        competitorMatched={competitorMatched}
        onContinue={rows => {
          setNormalized(rows);
          setStep('preview');
        }}
      />,
    );
  }

  if (step === 'preview') {
    return shell(
      'Review and import',
      backButton('Back to mapping', () => setStep('mapping')),
      <PreviewStep
        normalized={normalized}
        decisions={decisions}
        onDecisionsChange={setDecisions}
        onImport={() => setStep('importing')}
      />,
    );
  }

  if (step === 'importing') {
    return shell(
      'Importing clients',
      <div />,
      <ImportStep
        normalized={normalized}
        decisions={decisions}
        sourcePath={sourcePath}
        onComplete={r => {
          setResult(r);
          setStep('success');
        }}
      />,
    );
  }

  if (step === 'success' && result) {
    return shell(
      'All done',
      <div />,
      <SuccessStep
        result={result}
        rowsWithoutContact={
          normalized.filter(r => !r.phone && !r.email).length
        }
        onImportAnother={reset}
        onBackToSetupGuide={onSaved}
      />,
    );
  }

  // Fallback — should be unreachable.
  return shell(
    'Import from a spreadsheet',
    backButton('Import your clients', onBackToGrid),
    <UploadStep
      onParsed={p => {
        setParsed(p);
        setStep('mapping');
      }}
    />,
  );
}
